import { Atom, atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { NumberOption } from '@/types/generic';
import { objSnakeToCamel } from '@/utils/queries';
import { rawPlayersWithCacheFallbackAtom } from '../players';
import { allTeamsOptionsAtom } from '../teams';
import { playerComparisonCyclesAtom, playerComparisonCyclesOrderedAtom } from './playerComparisonCycles';

const playerComparisonDefaultCycleAtom = atom(async get => {
  const cycles = await get(playerComparisonCyclesOrderedAtom);
  if (!cycles.length) return null;
  return cycles[0];
});

export const playerComparisonInfoAtom = atom(async get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return null;

  const allPlayers = await get(rawPlayersWithCacheFallbackAtom);
  const compPlayer = allPlayers.find(player => player.player_id === comparisonId);
  if (!compPlayer) return null;

  return objSnakeToCamel(compPlayer);
});

export const playerComparisonDisplayNameAtom = atom(async get => {
  const info = await get(playerComparisonInfoAtom);
  return info?.displayName || '';
});

export const unwrappedPlayerComparisonDisplayNameAtom = unwrap(playerComparisonDisplayNameAtom, prev => prev || '');

export const playerComparisonCompetitionOptionsAtom: Atom<Promise<NumberOption[]>> = atom(async get => {
  const defaultCycle = await get(playerComparisonDefaultCycleAtom);
  if (!defaultCycle) return [];
  const {
    competition: { competitionId, name, areaCode },
    cycle: { has360 },
  } = defaultCycle;
  return [{ label: name, value: competitionId, optional: { countryCode: areaCode, is360: has360 } }];
});

export const playerComparisonSeasonOptionsAtom: Atom<Promise<NumberOption[]>> = atom(async get => {
  const defaultCycle = await get(playerComparisonDefaultCycleAtom);
  if (!defaultCycle) return [];
  const {
    season: { seasonId, name },
  } = defaultCycle;
  return [{ label: name, value: seasonId }];
});

export const playerComparisonTeamOptionsAtom: Atom<Promise<NumberOption[]>> = atom(async get => {
  const comparisonId = get(comparisonIdAtom);
  if (!comparisonId) return [];

  const [defaultCycle, allPlayers] = await Promise.all([
    get(playerComparisonDefaultCycleAtom),
    get(rawPlayersWithCacheFallbackAtom),
  ]);
  const playerComparison = allPlayers.find(player => player.player_id === comparisonId);
  if (!defaultCycle || !playerComparison) return [];

  const cycles = await get(playerComparisonCyclesAtom);
  const defaultCompId = defaultCycle.competition.competitionId;

  const matchingCycleIdSet = new Set(
    cycles.filter(cycle => cycle.competition.competitionId === defaultCompId).map(cycle => cycle.cycle.cycleId),
  );

  const teamIds = playerComparison.team_cycle_ids
    .filter(teamCycleMapping => teamCycleMapping.cycle_ids.some(cycleId => matchingCycleIdSet.has(cycleId)))
    .map(teamCycleMapping => teamCycleMapping.team_id);

  const teamIdSet = new Set(teamIds);
  const allTeamOptions = await get(allTeamsOptionsAtom);
  return allTeamOptions.filter(opt => teamIdSet.has(opt.value));
});
